import React, { useContext } from 'react'
import urljoin from 'url-join'

export type RestaurantRoutesContextType = {
  homePath: string
  orderPath: string
  ecommPath?: string
  baseEcommPath?: string
  ecommCheckoutPath?: string
  ecommConfirmationPath?: string
  orderPathPattern: string
  checkoutPath: string
  confirmationPath: string
  accountPath: string
}

export const RestaurantRoutesContext = React.createContext<
  RestaurantRoutesContextType | undefined
>(undefined)

export const getOrderPath = (shortUrl?: string | null) => `/order/${shortUrl}`
export const getMenuPath = (shortUrl?: string | null) =>
  `/menu${shortUrl ? '/' : ''}${shortUrl}`

/**
 * Gets the effective shortUrl for routing, with fallback to first valid location
 * @param shortUrl - The primary shortUrl (from props/params)
 * @param locations - Array of restaurant locations
 * @returns The effective shortUrl to use for routing
 */
export const getEffectiveShortUrl = (
  shortUrl?: string | null,
  locations?: Array<{ shortUrl?: string | null }> | null
): string => {
  // Return primary shortUrl if it's valid
  if (shortUrl && shortUrl.trim()) {
    return shortUrl
  }

  // Find the first location with a valid shortUrl
  const locationWithValidShortUrl = locations?.find(
    (location) => location.shortUrl && location.shortUrl.trim()
  )

  return locationWithValidShortUrl?.shortUrl || ''
}

/**
 * Builds the effective order path for routing
 * @param shortUrl - The primary shortUrl
 * @param locations - Array of restaurant locations
 * @param basePath - Base path (e.g., TOASTTAB_BOO_PATH)
 * @param fallbackPath - Fallback path when no shortUrl is available
 * @returns The complete order path
 */
export const getEffectiveOrderPath = (
  shortUrl: string | null | undefined,
  locations: Array<{ shortUrl?: string | null }> | null | undefined,
  basePath: string,
  fallbackPath: string
): string => {
  const effectiveShortUrl = getEffectiveShortUrl(shortUrl, locations)
  return effectiveShortUrl ? `${basePath}/${effectiveShortUrl}` : fallbackPath
}

/**
 * Validates if an account path is valid and safe to use
 * @param accountPath - The account path to validate
 * @returns true if the path is valid, false if it's garbage/unsafe
 */
export const isValidAccountPath = (accountPath: string): boolean => {
  if (!accountPath || !accountPath.trim()) {
    return false
  }

  // Detects double fallbacks (when both shortUrl and orderPath were missing)
  if (accountPath === '/online/account') {
    return false
  }

  // Detects when we fell back to basePath + empty shortUrl
  if (accountPath.match(/\/online\/\/account$/)) {
    return false
  }

  // Detects when orderPath fallback was used with missing shortUrl
  if (accountPath === '/online/online/account') {
    return false
  }

  // Detect patterns that suggest we used getEffectiveShortUrl but got empty result
  if (accountPath.match(/\/online\/account$/)) {
    return false
  }

  // Detects when we have nested fallback paths
  if (accountPath.includes('/online/online')) {
    return false // Indicates multiple fallbacks were applied
  }

  if (accountPath.includes('undefined') || accountPath.includes('null')) {
    return false
  }

  if (accountPath.includes('//')) {
    return false
  }

  if (accountPath.endsWith('/account/account')) {
    return false
  }

  if (!accountPath.includes('/account')) {
    return false
  }

  // URL structure validation
  try {
    new URL(accountPath, 'https://example.com')
    return true
  } catch {
    return false
  }
}

type Props = {
  shortUrl?: string | null
  homePath?: string
  orderPath?: string
  ecommPath?: string
  baseEcommPath?: string
  ecommCheckoutPathPrefix?: string
  ecommConfirmPathPrefix?: string
  orderPathPattern?: string
  checkoutPathPrefix?: string
  confirmPathPrefix?: string
  accountPath?: string
}

export const RestaurantRoutesContextProvider = ({
  shortUrl,
  homePath,
  orderPath,
  ecommPath,
  baseEcommPath,
  ecommCheckoutPathPrefix,
  ecommConfirmPathPrefix,
  orderPathPattern,
  checkoutPathPrefix,
  confirmPathPrefix,
  accountPath,
  children
}: React.PropsWithChildren<Props>) => {
  return (
    <RestaurantRoutesContext.Provider
      value={{
        homePath: homePath || '/',
        orderPath: orderPath || getOrderPath(shortUrl),
        baseEcommPath: baseEcommPath,
        ecommPath: ecommPath,
        ecommCheckoutPath: ecommCheckoutPathPrefix
          ? urljoin(
              ecommCheckoutPathPrefix,
              baseEcommPath && baseEcommPath !== '/' ? baseEcommPath : '',
              'checkout'
            )
          : urljoin(baseEcommPath ?? '', 'checkout'),
        ecommConfirmationPath: ecommConfirmPathPrefix
          ? urljoin(
              ecommConfirmPathPrefix,
              baseEcommPath && baseEcommPath !== '/' ? baseEcommPath : '',
              'confirm'
            )
          : urljoin(baseEcommPath ?? '', 'confirm'),
        orderPathPattern: orderPathPattern || '/order/:slug',
        checkoutPath: `${checkoutPathPrefix || ''}/checkout`,
        confirmationPath: `${confirmPathPrefix || ''}/confirm`,
        accountPath: accountPath || `${homePath || ''}/account`
      }}
    >
      {children}
    </RestaurantRoutesContext.Provider>
  )
}

export const useRestaurantRoutes = () => {
  const context = useContext(RestaurantRoutesContext)
  if (!context) {
    throw new Error(
      'useRestaurantRoutes must be used within a RestaurantRoutesContextProvider'
    )
  }

  return context
}
